import { Flex, Text, useToast } from '@chakra-ui/react';
import { ButtonIcon, FileUpload, FormFeedback } from '@morressier/alembic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { DropzoneOptions, ErrorCode } from 'react-dropzone';
import {
  deleteUploadedFile,
  getOrganizationSignedPrivateAccessUrl,
  getOrganizationSignedPrivateUploadUrl
} from 'src/api/queries';
import { FieldLabel } from '../Field';
import { AxiosProgressEvent } from 'axios';
import { FormFieldFileUploadArray as IFormFieldFileUploadArray } from '@publishing-engine/domain/types/article';
import { faFile, faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import axiosInstance from 'src/api/client';
import {
  getAllowedFileFormats,
  getFileExtension,
  parseUploadError
} from 'src/utils/file';
import { get } from 'lodash';
import { IFileUploaded } from '../FormField/common/types';

interface IFormFieldMultipleFileUploadProps {
  organizationId: string;
  config: Omit<IFormFieldFileUploadArray, 'component'>;
}

const MAX_FILES_AT_ONCE = 15;

const MultipleFileUpload: React.FC<IFormFieldMultipleFileUploadProps> = ({
  config,
  organizationId
}) => {
  const toast = useToast();
  const {
    control,
    setError,
    clearErrors,
    formState: { errors },
    register
  } = useFormContext<{ [key: string]: IFileUploaded[] }>();
  const {
    caseDataFields: { main: field },
    params: { labels, validations }
  } = config;
  const error = errors[field];
  const mainError = get(errors, field);

  const { append, remove, update, fields } = useFieldArray({
    control,
    name: field
  });

  const getUploadUrl = (extension: string) => {
    if (!organizationId) {
      throw new Error(
        'FormFieldMultipleFileUpload: no submission id. ' + field
      );
    }

    return getOrganizationSignedPrivateUploadUrl(organizationId, extension, true);
  };

  const handleOpenFile = async (url: string) => {
    if (!organizationId) {
      return;
    }
    const signedUrl = await getOrganizationSignedPrivateAccessUrl(
      organizationId,
      url
    );
    window.open(signedUrl, '_blank');
  };

  const uploadFile = async (file: File, index: number) => {
    const fileExtension = getFileExtension(file.name);
    let fileToUpload: IFileUploaded = {
      name: file.name,
      referenceName: file.name,
      progress: 0,
      isUploading: true,
      extension: fileExtension,
      url: ''
    };

    const onUploadProgress = () => (progressEvent: AxiosProgressEvent) => {
      const { loaded, total = Number.POSITIVE_INFINITY } = progressEvent;
      const progress = Math.floor((loaded * 100) / total);

      fileToUpload = {
        ...fileToUpload,
        progress
      };

      update(index, fileToUpload);
    };

    try {
      append(fileToUpload);

      const uploadParams = await getUploadUrl(fileExtension);
      if (!uploadParams) return;

      const formData = new FormData();
      formData.append('file', file, file.name);

      const {
        data: { id, file_url: url, original_filename }
      } = await axiosInstance.put<{
        id: string;
        file_url: string;
        original_filename: string;
      }>(uploadParams.signed_url, formData, {
        onUploadProgress: onUploadProgress()
      });

      fileToUpload = {
        ...fileToUpload,
        fileId: id,
        url,
        name: original_filename,
        referenceName: original_filename,
        extension: fileExtension
      };

      clearErrors(`${field}.${index}`);
      update(index, fileToUpload);
    } catch (e) {
      const message = parseUploadError(e);

      setError(`${field}.${index}`, {
        type: 'upload-error',
        message
      });
    } finally {
      update(index, {
        ...fileToUpload,
        isUploading: false
      });
    }
  };

  const removeFile = async (index: number, fileId?: string) => {
    remove(index);
    fileId && deleteUploadedFile(fileId);
  };

  const onDrop: DropzoneOptions['onDrop'] = async (acceptedFiles: File[]) => {
    clearErrors(field);
    const accepted = acceptedFiles;
    await Promise.all(
      accepted.map((file, index) => uploadFile(file, index + fields.length))
    );
  };

  const onDropRejected: DropzoneOptions['onDropRejected'] =
    async rejectedFiles => {
      if (!rejectedFiles || rejectedFiles.length === 0) return;

      // Separate rejected files into 'too many files' and other errors
      const tooManyFiles = rejectedFiles.filter(r =>
        r.errors.some(e => e.code === 'too-many-files')
      );
      const otherRejected = rejectedFiles.filter(
        r => !r.errors.some(e => e.code === 'too-many-files')
      );

      // Upload only allowed number of files from 'too many files'
      if (tooManyFiles.length > 0 && MAX_FILES_AT_ONCE > 0) {
        const filesToUpload = tooManyFiles
          .slice(0, MAX_FILES_AT_ONCE)
          .map(r => r.file);

        await Promise.all(
          filesToUpload.map((file, index) =>
            uploadFile(file, index + fields.length)
          )
        );
        toast({
          variant: 'attention',
          title: 'File Upload Limit Exceeded',
          description:
            'You can only upload up to ' +
            MAX_FILES_AT_ONCE +
            ' files at once. Excess files were not uploaded.',
          isClosable: true
        });
      }

      // Handle other rejected files (wrong type, too large, etc.)
      if (otherRejected.length > 0) {
        const messages = otherRejected.map(({ file, errors }) => {
          const reasons = errors.map(err => {
            switch (err.code) {
              case 'file-invalid-type':
                return 'Unsupported file type';
              case ErrorCode.FileTooLarge:
                return `File is larger than ${validations.maxSizeMB}MB`;
              default:
                return 'Upload rejected';
            }
          });
          return `${file.name}: ${reasons.join(', ')}`;
        });

        setError(field, {
          type: 'upload-rejected',
          message: messages.join(',')
        });
      }
    };

  const disableDeleteButton = () => {
    if (fields.length > 0) {
      return fields.some(file => file.isUploading);
    }
    return false;
  };

  const isDeleteButtonDisabled = disableDeleteButton();

  return (
    <Flex flexDirection="column" gap={8} width="100%">
      <Flex flexDirection="column" gap={2}>
        <Flex flexDirection="column">
          <FieldLabel
            isRequired={validations.required}
            label={labels.main}
            subText={labels.subtitle}
            helpText={labels.helpText}
          />
        </Flex>
        <Flex flexDirection="column" bgColor="surfacePrimaryFill">
          <FileUpload
            {...register(field)}
            uploadButtonText="Select files"
            multiple
            onDrop={onDrop}
            onDropRejected={onDropRejected}
            maxFiles={MAX_FILES_AT_ONCE}
            maxSize={validations.maxSizeMB * 1000000}
            isInvalid={Boolean(error?.message)}
            accept={validations.allowedFileFormats}
          />
        </Flex>
        <Text textStyle="textContext">
          {`Accepted format(s): ${getAllowedFileFormats(
            validations.allowedFileFormats
          )}. Max size: ${validations.maxSizeMB}MB. Executable scripts are not permitted.`}
        </Text>
      </Flex>

      {fields.length ? (
        <Flex flexDirection="column">
          {fields.map((file, index) => (
            <Flex
              key={file.id || file.url}
              flexDirection="column"
              borderBottom="1px"
              _last={{ borderBottom: 'none' }}
              padding={4}
              gap={2}
            >
              <Flex
                flexDirection="row"
                justifyContent="space-between"
                width="100%"
              >
                <Flex alignItems="center" gap={2} flexDirection="row">
                  <FontAwesomeIcon icon={faFile} fontSize={20} />
                  <a
                    onClick={() => handleOpenFile(file.url)}
                    style={{ cursor: 'pointer' }}
                  >
                    <Text textStyle="textBody">{file.name}</Text>
                  </a>
                </Flex>
                {file.isUploading ? (
                  <Text textStyle="textContext">Uploading</Text>
                ) : (
                  <ButtonIcon
                    size="regular"
                    ariaLabel={`${file.name} delete`}
                    onClick={() => removeFile(index, file.id)}
                    disabled={isDeleteButtonDisabled}
                    icon={<FontAwesomeIcon icon={faTrashAlt} />}
                  />
                )}
              </Flex>
              {!file.isUploading && mainError?.[index] && (
                <FormFeedback variant="error">
                  {mainError?.[index]?.message}
                </FormFeedback>
              )}
            </Flex>
          ))}
        </Flex>
      ) : null}
      {mainError?.type === 'upload-rejected' && (
        <Flex flexDirection="column" gap={2}>
          {(mainError?.message?.split(',') as string[]).map((msg, i) => (
            <FormFeedback key={i} variant="error">
              {msg}
            </FormFeedback>
          ))}
        </Flex>
      )}
    </Flex>
  );
};

export default MultipleFileUpload;
